import { Component, ElementRef, Input, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { Chart, ChartConfiguration, ChartDataset } from 'chart.js';
import { TimeFrameMode } from 'src/app/shared/interfaces/global-filter-model';
import { ChartHandler } from 'src/app/shared/handlers/chart-handler';
import { KeyGenerator } from 'src/app/shared/handlers/key-generator';
import { PrecisionType } from 'src/app/shared/interfaces/chart.model';
import { GlobalFilterService } from 'src/app/shared/services/global-filter.service';

@Component({
  selector: 'app-scrollable-chart',
  templateUrl: './scrollable-chart.component.html',
  styleUrls: ['./scrollable-chart.component.scss'],
})
export class ScrollableChartComponent {
  @Input() chartData;
  @Input() chartParams: any;
  @Input() timeFrameMode: TimeFrameMode;
  @Input() defaultYearRange = false;
  chartId: string = KeyGenerator.generate(5);

  public scrollChart: any;
  public fixedChart: any;

  private yearRange = {
    startYear: undefined,
    endYear: undefined,
  };
  private subs = new Subscription();

  @ViewChild('chartBox', { static: false }) chartBox: ElementRef;

  constructor(
    private globalFilterService: GlobalFilterService,
    private renderer: Renderer2,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.timeFrameMode || Object.keys(this.chartData[this.timeFrameMode]).length > 0) {
      setTimeout(() => {
        this.prepareChart();
      }, 100);
    }
  }

  ngOnInit(): void {
    this.subs.add(
      this.globalFilterService.globalFilters.subscribe((filter) => {
        if (filter) {
          this.yearRange.startYear = filter.start_year;
          this.yearRange.endYear = filter.end_year;
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.fixedChart?.destroy();
    this.scrollChart?.destroy();
  }

  private prepareChart() {
    this.scrollChart?.destroy();
    this.fixedChart?.destroy();
    if (this.chartData == undefined || Object.keys(this.chartData[this.timeFrameMode]).length == 0) {
      return;
    }

    const fixedChartConfig: ChartConfiguration = {
      type: 'bar',
      data: {
        labels: this.getLabels(),
        datasets: this.getDataSetsForFixed(),
      },
      options: {
        maintainAspectRatio: false,
        layout: {
          padding: {
            bottom: this.timeFrameMode == TimeFrameMode.YEARLY ? 22 : 28,
            top: 20,
          },
        },
        scales: {
          x: {
            stacked: this.chartParams[0].stacked,
            grid: {
              display: false,
              drawOnChartArea: false,
              drawTicks: false,
            },
            beginAtZero: true,
            ticks: {
              display: false,
            },
            border: {
              display: false,
            },
          },
          y: {
            grace: 2,
            stacked: this.chartParams.length > 1 ? this.chartParams[1].stacked : this.chartParams[0].stacked,
            title: {
              display: true,
              text: this.chartParams.length > 1 ? this.chartParams[1].titleY : '',
              font: {
                size: 16,
                weight: 'bold',
                family: 'Calibri',
              },
              color: '#281138',
            },
            type: 'linear',
            position: 'left',
            display: 'display' in this.chartParams[this.chartParams.length > 1 ? 1 : 0] ? this.chartParams[this.chartParams.length > 1 ? 1 : 0].display : true,
            ticks: {
              display: 'showYAxis' in this.chartParams[this.chartParams.length > 1 ? 1 : 0] ? this.chartParams[this.chartParams.length > 1 ? 1 : 0].showYAxis : true,
              font: {
                size: 18,
                weight: 'bold',
                family: 'Calibri',
                lineHeight: 'normal',
              },
              color: '#212121',
              callback: (value: any, index: any, ticks: any) => {
                const suffix = 'unit' in this.chartParams[this.chartParams.length > 1 ? 1 : 0] ? this.chartParams[this.chartParams.length > 1 ? 1 : 0].unit : '';
                return value + suffix;
              },
            },
            grid: {
              display: false,
              drawOnChartArea: false,
            },
            border: {
              display: false,
            },
            beginAtZero: true,
            afterFit: (ctx) => {
              ctx.width = 80;
            },
          },
        },
        plugins: {
          datalabels: {
            display: false,
          },
          legend: {
            display: false,
          },
        },
      },
    };
    const yAxisVisible = 'showYAxis' in this.chartParams[0] ? this.chartParams[0].showYAxis : true;
    if (this.chartParams[0].type == 'line' && yAxisVisible) {
      fixedChartConfig.options.scales['A'] = {
        grace: 2,
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        title: {
          text: this.chartParams[0].titleY,
          display: true,
          font: {
            size: 16,
            weight: 'bold',
            family: 'Calibri',
          },
          color: '#281138',
        },
        grid: {
          display: true,
          drawOnChartArea: false,
          drawTicks: false,
        },
        border: {
          display: false,
        },

        ticks: {
          display: 'showYAxis' in this.chartParams[0] ? this.chartParams[0].showYAxis : true,
          font: {
            size: 18,
            weight: 'bold',
            family: 'Calibri',
            lineHeight: 'normal',
          },
          color: '#212121',
          callback: (value: any, index: any, ticks: any) => {
            const suffix = 'unit' in this.chartParams[0] ? this.chartParams[0].unit : '%';
            return value + suffix;
          },
        },

        afterFit: (ctx) => {
          ctx.width = 70;
        },
      };
    }
    this.fixedChart = new Chart(this.chartId + 'fixedChart', fixedChartConfig);

    const scrollChartConfig: ChartConfiguration = {
      type: 'bar',
      data: {
        labels: this.getLabels(),
        datasets: this.getDataSets(),
      },
      options: {
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 35,
            bottom: 0,
            left: 10,
          },
        },
        scales: {
          x: {
            stacked: this.chartParams[0].stacked,
            ticks: {
              display: true,
              font: {
                size: 14,
                weight: 'bold',
                family: 'Calibri',
                lineHeight: 'normal',
              },
              color: '#212121',
            },
            beginAtZero: true,
            grid: {
              display: false,
              drawTicks: false,
            },
          },
          y: {
            grace: 2,
            stacked: this.chartParams[this.chartParams.length > 1 ? 1 : 0].stacked,
            title: {
              display: false,
            },
            ticks: {
              display: false,
            },
            border: {
              display: false,
            },
            grid: {
              display: true,
              drawTicks: false,
            },
            beginAtZero: true,
          },
        },
        plugins: {
          datalabels: {
            display: 'true',
            // display only to last stacked item mapped with selected legend item
            opacity: (context) => {
              if (context.dataset.stack == '0') {
                const stackedDatasetLabels = context.chart.data.datasets.filter((dataset) => dataset.stack == context.dataset.stack).map((el) => el.label);
                if (
                  context.dataset.type === 'line' ||
                  context.datasetIndex ===
                    context.chart.legend.legendItems
                      .filter((el) => stackedDatasetLabels.includes(el.text))
                      .map((el) => !el.hidden)
                      .lastIndexOf(true)
                ) {
                  return 1;
                } else {
                  return 0;
                }
              } else {
                const lastVisibleIndex = context.chart.legend.legendItems.map((el) => !el.hidden).lastIndexOf(true);
                let lastVisiblePositiveDataSetIndex = lastVisibleIndex;
                for (let i = lastVisiblePositiveDataSetIndex; i > -1; i--) {
                  const dataValue: number = context.chart.data.datasets[i].data[context.dataIndex] as any;
                  //if current bar is visible and any previous bar is visible
                  const isOtherIndexVisibleWithCurrentStack = Array.from({ length: i }, (_, index) => index).some(
                    (el) => context.dataset.stack === context.chart.data.datasets[el].stack && context.chart.isDatasetVisible(el),
                  );
                  if ((dataValue < 0 || !context.chart.isDatasetVisible(i)) && isOtherIndexVisibleWithCurrentStack) {
                    lastVisiblePositiveDataSetIndex--;
                  } else {
                    break;
                  }
                }

                if (context.dataset.type === 'line' || context.datasetIndex === lastVisiblePositiveDataSetIndex) {
                  // if (context.dataset.type === 'line' || context.datasetIndex === context.chart.legend.legendItems.map((el) => !el.hidden).lastIndexOf(true)) {
                  return 1;
                } else {
                  return 0;
                }
              }
            },

            formatter: (value: any, context) => {
              const datasetArray = [];
              const visibleDataitem = context.chart.legend.legendItems.filter((el) => !el.hidden).map((el) => el.text);
              context.chart.data.datasets.forEach((dataset) => {
                //only stacked item and selected legend item selected
                if (dataset.type != 'line' && dataset.data[context.dataIndex] != undefined && visibleDataitem.includes(dataset.label)) {
                  if (dataset.stack == context.dataset.stack) {
                    datasetArray.push(dataset.data[context.dataIndex]);
                  }
                }
              });
              let sum = datasetArray.reduce((sum, item) => sum + item, 0);

              return sum == 0 ? 0 : sum.toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            },
          },
          legend: {
            display: false,

            labels: {
              usePointStyle: true,
              textAlign: 'right',
            },
          },
        },
      },
    };

    if (this.chartParams[0].type == 'line' && yAxisVisible) {
      scrollChartConfig.options.scales['A'] = {
        grace: 2,
        display: false,
        ticks: {
          display: false,
        },
        title: {
          display: false,
        },
        border: {
          display: false,
        },
        grid: {
          display: false,
          drawOnChartArea: false,
        },
        beginAtZero: true,
      };
    }
    this.scrollChart = new Chart(this.chartId + 'scrollChart', scrollChartConfig);

    if (this.timeFrameMode == TimeFrameMode.YEARLY) {
      this.renderer.setStyle(this.chartBox.nativeElement, 'width', `${this.fixedChart?.width - 150}px`);
    } else {
      this.renderer.setStyle(this.chartBox.nativeElement, 'width', '3500px');
    }

    ChartHandler.generateLegend(this.chartId, this.scrollChart, this.fixedChart, this.chartParams[0]?.legend?.isTop, this.chartParams[0]?.legend?.isRight);
  }

  private getLabels() {
    const labels = [];
    for (let year = this.yearRange.startYear; year < this.yearRange.endYear + 1; year++) {
      if (this.timeFrameMode == TimeFrameMode.QUARTERLY) {
        for (let q = 1; q <= 4; q++) {
          labels.push(year + '-Q' + q);
        }
      } else {
        labels.push(year);
      }
    }
    return labels;
  }

  private getDataSets() {
    let dataSet: ChartDataset[] = [];

    this.chartParams.forEach((key) => {
      if (key.type === 'line') {
        dataSet.push({
          label: key.label,
          data: this.calculateVals(key.name, key?.precision || PrecisionType.NONE, key?.precisionInverse),
          borderColor: key?.border || key.color,
          backgroundColor: key?.bg || '#EAEAF2',
          borderWidth: 2,
          type: 'line',
          pointBackgroundColor: key?.pointBg || '#FFFFFF',
          pointBorderColor: key.color,
          pointBorderWidth: 2,
          pointRadius: 8,
          pointStyle: 'circle',
          yAxisID: key?.yAxisId || (this.chartParams[this.chartParams.length > 1 ? 1 : 0].type == 'line' ? 'y' : 'A'),
          order: 1,
          datalabels: {
            display: 'true',
            color: key?.dataLabel?.color || '#EAEAF2',
            backgroundColor: key?.dataLabel?.bg || '#747480',
            borderColor: key?.dataLabel?.border || '#2E2E38 ',
            borderRadius: 41,
            borderWidth: 1,
            anchor: 'end',
            align: 'top',
            font: {
              size: 14,
              weight: 'bold',
            },
            padding: {
              top: 2,
              bottom: 2,
              left: 10,
              right: 10,
            },
            formatter: (value: any) => {
              const suffix = 'unit' in key ? key.unit : '%';
              const roundingDigit = key?.titleY?.includes('C:D') || key?.label?.includes('C:D') || key?.titleY?.includes('YoY') || key?.label?.includes('YoY') ? 2 : 1;
              return value == 0 ? 0 + suffix : value.toFixed(roundingDigit).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + suffix;
              // return value + suffix;
            },
          },
        });
      } else {
        dataSet.push({
          label: key.label,
          backgroundColor: key?.color,
          data: this.calculateVals(key.name, key?.precision, key?.precisionInverse),
          borderRadius: 4,
          maxBarThickness: 70,
          pointStyle: 'rect',
          order: 2,
          stack: key.stack,
          datalabels: {
            color: key?.dataLabel?.color || '#2E2E38 ', // Color of the data labels
            anchor: 'end', // Position of the labels: start, center, end
            align: 'top', // Alignment of the labels: start, center, end
            backgroundColor: key?.dataLabel?.bg || '#EAEAF2',
            borderColor: key?.dataLabel?.border || '#292928',
            borderRadius: 20,
            borderWidth: 1,
            font: {
              size: 14,
              weight: 'bold',
              family: 'Calibri',
              lineHeight: 'normal',
            },
            padding: {
              top: 4,
              bottom: 4,
              left: 8,
              right: 8,
            },
          },
        });
      }
    });
    return dataSet;
  }

  private getDataSetsForFixed() {
    let dataSet: ChartDataset[] = [];

    this.chartParams.forEach((key) => {
      if (key.type === 'line') {
        dataSet.push({
          label: key.label,
          type: 'line',
          // data: this.chartData[this.timeFrameMode][key.name].map((el) => parseFloat(el.toFixed(1))),
          data: this.calculateVals(key.name, key?.precision || PrecisionType.NONE, key?.precisionInverse),
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          pointBackgroundColor: 'transparent',
          yAxisID: key?.yAxisId || (this.chartParams[this.chartParams.length > 1 ? 1 : 0].type == 'line' ? 'y' : 'A'),
        });
      } else {
        dataSet.push({
          label: key.label,
          backgroundColor: 'transparent',
          stack: key.stack,
          data: this.calculateVals(key.name, key?.precision),
        });
      }
    });
    return dataSet;
  }

  private calculateVals(type, precision: PrecisionType = PrecisionType.MILLIONTH, precisionInverse = false) {
    if (
      this.chartData != undefined &&
      this.chartData[this.timeFrameMode] != undefined &&
      this.chartData[this.timeFrameMode][type] != undefined &&
      this.chartData[this.timeFrameMode][type].length > 0
    ) {
      return this.chartData[this.timeFrameMode][type].map((amt) => {
        if (!amt) return 0;
        let value;
        if (precisionInverse) {
          value = amt * precision;
        } else {
          value = amt / precision;
        }
        return parseFloat(value.toFixed(2));
      });
    } else {
      return Array(this.getLabels().length).fill(0);
    }
  }
}
